import type { BillingPlan } from 'definitions/billing-plans';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from 'hooks/useAuth';
import { useClient } from 'hooks/useClient';
import { BILLING_STATE_KEY } from './useBillingState';
import { readApiError } from './api-error';

export type BillingAllowance = {
  plan: BillingPlan;
  /** Sessions the plan includes per window, or null for Unlimited. */
  included: number | null;
  /** Billable website sessions counted so far in the window. */
  used: number;
  windowStart: string;
  /** When the window rolls over and the count starts again. */
  windowEnd: string;
  /** Where the count is heading by the window's end at the pace so far, or null in the first days. */
  projected: number | null;
  /** A move up the account was told about and can decline, or null. See the server's AllowanceUsage. */
  plannedUpgrade: { plan: BillingPlan; appliesAt: string } | null;
  /** The account chose to stay on this plan: no move up, and its sessions are capped at the allowance. */
  held: boolean;
  /** The held account has used its sessions: experiments and surveys are paused until the window ends. */
  capped: boolean;
};

type UseBillingAllowanceParams = {
  /** An agency's client; the caller's own account when omitted. */
  accountId?: number | null;
};

/**
 * The session allowance of one account's plan, in its own window. Backed by
 * `GET /api/billing/allowance`; `null` data means there is no plan to measure
 * (a free or invoice account, one with no plan yet, or an agency itself).
 */
export const useBillingAllowance = ({ accountId = null }: UseBillingAllowanceParams = {}) => {
  const apiClient = useClient();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const {
    data: allowance,
    isLoading,
    isError,
    refetch,
  } = useQuery<BillingAllowance | null>({
    queryKey: ['BILLING_ALLOWANCE', accountId ?? 'self'],
    enabled: !!user,
    queryFn: () => apiClient.get(accountId ? `/billing/allowance?accountCompanyId=${accountId}` : '/billing/allowance'),
  });

  // "Keep my plan": declines the move up the account was told about.
  const keepPlanMutation = useMutation({
    mutationFn: () =>
      apiClient.post(accountId ? `/billing/keep-plan?accountCompanyId=${accountId}` : '/billing/keep-plan'),
    onSuccess: () => toast.success('The plan stays as it is.'),
    onError: (error: unknown) => {
      const { status, message } = readApiError(error);
      toast.error('Could not keep your plan', {
        description: status && status >= 400 && status < 500 && message ? message : 'Please try again in a moment.',
      });
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['BILLING_ALLOWANCE'] }),
        queryClient.invalidateQueries({ queryKey: BILLING_STATE_KEY }),
      ]),
  });

  return {
    allowance: allowance ?? null,
    isLoading,
    isError,
    refetch,
    keepPlan: () => keepPlanMutation.mutate(),
    isKeepingPlan: keepPlanMutation.isPending,
  };
};
